/**
 * The shape language, as functions rather than remembered values.
 *
 * Corners are even and faces are round, both defined in tokens.css. A value
 * repeated by hand across twenty components drifts, so components take their
 * radii from here and their face crop from `--radius-face`.
 */

/** Corner radii. Even on purpose; see tokens.css. */
export const radius = {
  xs: 'var(--radius-xs)',
  sm: 'var(--radius-sm)',
  md: 'var(--radius-md)',
  lg: 'var(--radius-lg)',
  xl: 'var(--radius-xl)',
  action: 'var(--radius-action)',
} as const

export type RadiusName = keyof typeof radius

export interface FaceAppearance {
  borderRadius: string
  background: string
  boxShadow: string
  opacity: number
}

export interface Presence {
  boxShadow: string
  opacity: number
}

/**
 * How a face shows whether its agent is here.
 *
 * One definition, because there are two kinds of face — a pool slot and an
 * uploaded portrait — and an agent that fades on one and not the other reads
 * as a rendering bug rather than as absence. Offline fades rather than greying
 * further: the palette has nowhere greyer to go.
 */
export function presence({ online = true, ring }: { online?: boolean; ring?: string } = {}): Presence {
  return {
    boxShadow: online ? `0 0 0 1.5px ${ring ?? 'rgba(255,255,255,.34)'}` : 'none',
    opacity: online ? 1 : 0.42,
  }
}

/**
 * How many faces a style has.
 *
 * Must match `FACE_SLOT_COUNT` in the server's `services.py` and the number of
 * files in each directory under `web/public/faces` — a slot means the same
 * thing whichever style an organisation is on, which is only true if all three
 * agree.
 */
export const FACE_SLOT_COUNT = 36

/**
 * The six pattern families.
 *
 * **These are drawn for 22px, not for 84px.** An earlier version used six fine
 * repeating textures — 3px stripes, a 7px dot grid — which are handsome on the
 * settings page and completely invisible in the face pile, where four agents
 * came out as four identical discs. Texture needs more pixels than a face pile
 * has.
 *
 * So the family is a *gross shape* with a silhouette: solid, split, bars, dot,
 * ring, quarters. Each survives being 22 pixels wide, and none of them spends
 * colour, so the one-accent rule in tokens.css holds.
 */
const FACE_PATTERNS: ReadonlyArray<(pair: readonly [string, string]) => string> = [
  ([light]) => `linear-gradient(0deg,${light},${light})`,
  ([light, dark]) => `linear-gradient(135deg,${light} 0 50%,${dark} 50% 100%)`,
  ([light, dark]) => `linear-gradient(90deg,${light} 0 33%,${dark} 33% 66%,${light} 66% 100%)`,
  ([light, dark]) => `radial-gradient(circle at 50% 50%,${light} 0 38%,${dark} 38%)`,
  ([light, dark]) => `radial-gradient(circle at 50% 50%,${dark} 0 22%,${light} 22% 44%,${dark} 44%)`,
  ([light, dark]) => `conic-gradient(${light} 0 25%,${dark} 0 50%,${light} 0 75%,${dark} 0)`,
]

/**
 * The value pairs the pool is drawn from.
 *
 * Spread deliberately wide — #171717 to #7a7a7a — because at 22px light versus
 * dark separates two faces before the shape does. The previous set lived
 * between #22 and #3a, a range narrow enough that it carried no information at
 * any size. Still grey: a wider range of grey is not decorative colour.
 */
const POOL_GREYS: ReadonlyArray<readonly [string, string]> = [
  ['#2b2b2b', '#171717'],
  ['#3a3a3a', '#202020'],
  ['#4a4a4a', '#282828'],
  ['#5a5a5a', '#303030'],
  ['#6a6a6a', '#3a3a3a'],
  ['#7a7a7a', '#454545'],
]


/**
 * What one slot of the `patterns` style looks like.
 *
 * Slots are 1-based, to match the `face:NN` keys the server writes. Anything
 * outside 1..FACE_SLOT_COUNT wraps rather than throwing: a face is cosmetic,
 * and an exception here would take down a whole room over a decoration.
 */
export function patternFace(
  slot: number,
  options: { online?: boolean; ring?: string } = {},
): FaceAppearance {
  const index = (((Math.trunc(slot) - 1) % FACE_SLOT_COUNT) + FACE_SLOT_COUNT) % FACE_SLOT_COUNT
  const family = index % FACE_PATTERNS.length
  const pair = POOL_GREYS[Math.floor(index / FACE_PATTERNS.length) % POOL_GREYS.length]
  return {
    borderRadius: 'var(--radius-face)',
    background: FACE_PATTERNS[family](pair),
    ...presence(options),
  }
}
